import type { LayoutSnapshot, Resolved, SnapshotNode, Viewport } from '@personalizehq/schemas/ir';

// READ THE DOM BACK. This is what conformance diffs against the committed goldens, and what
// makes a green run mean something under rects positioning.
//
// THE CIRCULARITY THIS FILE EXISTS TO AVOID: the solver imposes every frame, so measuring the
// painted box just reads the solver's own answer back — a gate that can never fail. The honest
// signals are the ones the DOM decides for itself:
//
//   line_count  the INNER element's natural flow height (the browser's OWN wrap decision),
//               NOT the imposed outer frame. This is the real cross-check, and it is
//               engine-local: WebKit's canvas measurement vs WebKit's own line breaking.
//   resolved    read from getComputedStyle wherever CSS exposes the value (bg, color, font
//               size, line height) rather than from an attribute we stamped ourselves.
//
// Border/shadow/tint colours live inside composite `box-shadow`/overlay values that
// getComputedStyle does not decompose usefully, so those three come from stamped data
// attributes (the spike's precedent). That residual is circular and is accepted: their VALUE
// correctness is proven by the four native renderers agreeing on the same goldens.

/** `rgb(124, 147, 255)` -> `#7C93FF`. The comparator normalizes too, but a canonical snapshot
 *  keeps the committed artifacts readable. */
function hex(v: string): string {
  const m = v.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!m) return v;
  return `#${[m[1], m[2], m[3]]
    .map((c) => Number(c).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

const round = (n: number) => Math.round(n * 100) / 100;

export function domSnapshot(layer: HTMLElement, vp: Viewport): LayoutSnapshot {
  const origin = layer.getBoundingClientRect();
  const nodes: SnapshotNode[] = [];

  for (const el of Array.from(layer.querySelectorAll<HTMLElement>('[data-phq-id]'))) {
    const r = el.getBoundingClientRect();
    const cs = getComputedStyle(el);
    const node: SnapshotNode = {
      id: el.dataset.phqId!,
      frame: [
        round(r.left - origin.left),
        round(r.top - origin.top),
        round(r.width),
        round(r.height),
      ],
    };

    if (el.dataset.phqScrolls) {
      node.scrolls = el.dataset.phqScrolls as 'vertical' | 'horizontal';
    }

    const resolved: Resolved = {};

    if (el.dataset.phqKind === 'text') {
      // THE NON-CIRCULAR MEASUREMENT. The inner element has no imposed height, so its natural
      // flow height IS the browser's own line breaking. line_height is normative and pinned in
      // px, so height / lineHeight is exactly the line count.
      const inner = el.firstElementChild as HTMLElement | null;
      const lineHeight = parseFloat(cs.lineHeight);
      if (inner && lineHeight > 0) {
        node.line_count = Math.round(inner.getBoundingClientRect().height / lineHeight);
      }
      resolved.color = hex(cs.color);
      // Rounded: WebKit serializes computed font sizes as 20.799999 where Chromium says 20.8 —
      // the same number, a different float printing. Not a divergence; do not gate on it.
      resolved.font_size = round(parseFloat(cs.fontSize));
      resolved.line_height = round(lineHeight);
    } else if (el.dataset.phqKind === 'button') {
      resolved.color = hex(cs.color);
      resolved.font_size = round(parseFloat(cs.fontSize));
      resolved.line_height = round(parseFloat(cs.lineHeight));
    }

    // `transparent` / `rgba(0,0,0,0)` means "no background token", not "black".
    if (cs.backgroundColor && !/^rgba\(0,\s*0,\s*0,\s*0\)$/.test(cs.backgroundColor)) {
      resolved.bg = hex(cs.backgroundColor);
    }
    // Stamped, not computed: under a gradient `background-color` is TRANSPARENT, so the computed
    // style tells the snapshot nothing and the light/dark pair would gate nothing (SPEC §5.0c).
    if (el.dataset.phqBgStops) resolved.bg_stops = el.dataset.phqBgStops.split('|').map(hex);
    if (el.dataset.phqBorderColor) resolved.border_color = hex(el.dataset.phqBorderColor);
    if (el.dataset.phqShadowColor) resolved.shadow_color = hex(el.dataset.phqShadowColor);
    if (el.dataset.phqTint) resolved.tint = hex(el.dataset.phqTint);

    if (Object.keys(resolved).length > 0) node.resolved = resolved;
    nodes.push(node);
  }

  return { viewport: vp, nodes };
}

/** THE GATE the rects architecture actually earns: for every text node, the solver's canvas
 *  measurement and the browser's own line breaking must agree. Under rects, a frame comparison
 *  is near-tautological for non-text nodes — THIS is the signal. Engine-local, so it stays
 *  sharp on WebKit even though the goldens were recorded on Chromium. */
export function assertTextAgreement(solver: LayoutSnapshot, dom: LayoutSnapshot): void {
  const byId = new Map(dom.nodes.map((n) => [n.id, n]));
  const bad: string[] = [];
  for (const s of solver.nodes) {
    if (s.line_count === undefined) continue;
    const d = byId.get(s.id);
    if (d?.line_count !== undefined && d.line_count !== s.line_count) {
      bad.push(`${s.id}: solver ${s.line_count} vs DOM ${d.line_count}`);
    }
  }
  if (bad.length > 0) {
    throw new Error(
      `canvas measurement disagrees with the browser's own line breaking:\n  ${bad.join('\n  ')}\n` +
        `This is a REAL divergence in the injected text primitive — do not widen a tolerance.`
    );
  }
}
